/**
 * Technicians & Skills — redesigned with sidebar layout + shared SkillsMatrix.
 * Left sidebar: searchable technician list.
 * Main panel: selected technician's skills (expandable groups) + quick stats.
 */
import React from 'react';
import { RefreshCw, Download, Search, Phone, Ticket as TicketIcon, Wrench, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@hts/ui';
import { useGet, apiPatch, apiPost } from '../../hooks/useApi.js';
import { useAuth } from '../../hooks/useAuth.js';
import { CardSkeleton } from '../../components/ui/Skeleton.js';
import { EmptyState, ErrorState } from '../../components/ui/EmptyState.js';
import { SkillsMatrix, type SkillEntry } from '../../components/SkillsMatrix.js';
import type { Technician, Skill, SkillLevel } from '../../api/types.js';

const LEVEL_COLORS: Record<number, string> = {
  0: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
  1: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  2: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
  3: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  4: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
};

export function TechniciansPage() {
  const { data: techData, loading: techLoading, error: techError, refetch: refetchTechs } = useGet<{ items: Technician[] }>('/api/v1/technicians');
  const { data: skillsData } = useGet<{ items: Skill[] }>('/api/v1/skills');
  const auth = useAuth();
  const role = auth.status === 'authenticated'
    ? auth.user.memberships.find(m => m.tenantId === auth.user.activeTenantId)?.role
    : undefined;
  const isAdmin = role === 'admin' || role === 'owner';
  const canEdit = isAdmin || role === 'dispatcher';
  const [busy, setBusy] = React.useState<'sync' | 'import' | null>(null);
  const [search, setSearch] = React.useState('');
  const [selectedTechId, setSelectedTechId] = React.useState<string | null>(null);

  const techs = techData?.items ?? [];

  const filteredTechs = React.useMemo(() => {
    if (!search.trim()) return techs;
    const q = search.toLowerCase();
    return techs.filter((t) => t.name.toLowerCase().includes(q) || (t.role ?? '').toLowerCase().includes(q));
  }, [techs, search]);

  const selectedTech = techs.find((t) => t.id === selectedTechId) ?? filteredTechs[0] ?? null;

  async function syncTechnicians() {
    setBusy('sync');
    try {
      const r = await apiPost<{ created: number; total: number }>('/api/v1/technicians/sync-cw', {});
      toast.success(r.created > 0 ? 'Added ' + r.created + ' technician(s) from ConnectWise' : 'Technicians already up to date (' + r.total + ' active)');
      refetchTechs();
    } catch (e) { toast.error('Sync failed: ' + (e instanceof Error ? e.message : 'request failed')); }
    finally { setBusy(null); }
  }

  async function importSkills() {
    setBusy('import');
    try {
      const r = await apiPost<{ created: number; total: number }>('/api/v1/skills/import-cw', {});
      toast.success(r.created > 0 ? 'Imported ' + r.created + ' skill(s) from ConnectWise' : 'Skills already match ConnectWise');
    } catch (e) { toast.error('Import failed: ' + (e instanceof Error ? e.message : 'request failed')); }
    finally { setBusy(null); }
  }

  async function setTechSkill(skillId: string, level: number) {
    if (!selectedTech) return;
    try {
      await apiPatch('/api/v1/technicians/' + selectedTech.id + '/skills/' + skillId, { level, approved: true });
      refetchTechs();
    } catch (e) { toast.error('Save failed: ' + (e instanceof Error ? e.message : 'request failed')); }
  }

  async function updateCapacity(techId: string, value: string) {
    try { await apiPatch('/api/v1/technicians/' + techId + '/capacity', { maxOpenTickets: value ? Number(value) : null }); toast.success('Capacity saved'); refetchTechs(); }
    catch (e) { toast.error('Save failed: ' + (e instanceof Error ? e.message : 'request failed')); }
  }

  if (techLoading && !techData) return <div className="p-6 space-y-3">{Array.from({ length: 4 }).map((_, i) => <CardSkeleton key={i} lines={3} />)}</div>;
  if (techError) return <ErrorState message={'Failed to load technicians (' + techError + ').'} onRetry={refetchTechs} />;

  if (techs.length === 0) {
    return (
      <div className="p-6">
        <EmptyState
          icon="👷"
          title="No technicians yet"
          description={isAdmin ? 'Technician records are created from ConnectWise members. Run "Sync technicians" after the ConnectWise member sync has completed.' : 'An admin needs to sync technicians from ConnectWise.'}
          action={isAdmin ? (
            <button onClick={syncTechnicians} disabled={busy !== null} className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50">
              {busy === 'sync' ? 'Syncing…' : 'Sync technicians from ConnectWise'}
            </button>
          ) : undefined}
        />
      </div>
    );
  }

  // Merge ALL skills from taxonomy with the tech's set skills
  // So every skill shows (even unset ones at level 0)
  const allSkills = skillsData?.items ?? [];
  const techSkillMap = new Map((selectedTech?.skills ?? []).map((s) => [s.skillId, s]));
  const techSkills: SkillEntry[] = selectedTech ? allSkills.map((skill) => {
    const ts = techSkillMap.get(skill.id);
    return {
      skillId: skill.id,
      skillName: skill.name,
      category: skill.category ?? 'Uncategorized',
      level: ts ? (ts.level as number) : 0,
      source: ts?.inferred ? 'inferred' : (ts?.source ?? 'unassigned'),
      inferred: ts?.inferred ?? false,
      confidence: ts?.confidence,
    };
  }) : [];

  const expertCount = techSkills.filter((s) => s.level >= 4).length;
  const assignedCount = techSkills.filter((s) => s.level > 0).length;
  const inferredCount = techSkills.filter((s) => s.inferred).length;

  return (
    <div className="flex h-[calc(100vh-3.5rem)]">
      {/* Sidebar: technician list */}
      <aside className="w-72 shrink-0 border-r border-[hsl(var(--border))] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-[hsl(var(--border))]">
          <h1 className="text-lg font-bold text-[hsl(var(--content))]">Technicians</h1>
          <p className="text-xs text-[hsl(var(--content-subtle))]">{techs.length} total</p>
        </div>
        {/* Search */}
        <div className="p-3 border-b border-[hsl(var(--border))]">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[hsl(var(--content-subtle))]" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search technicians…"
              className="w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] pl-8 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
        </div>
        {/* Actions */}
        {isAdmin && (
          <div className="p-3 border-b border-[hsl(var(--border))] flex gap-2">
            <button onClick={syncTechnicians} disabled={busy !== null} title="Sync from ConnectWise"
              className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium border border-[hsl(var(--border))] text-[hsl(var(--content-subtle))] hover:bg-[hsl(var(--surface-subtle))] disabled:opacity-50">
              <RefreshCw className={cn('h-3 w-3', busy === 'sync' && 'animate-spin')} /> Sync
            </button>
            <button onClick={importSkills} disabled={busy !== null} title="Import skills from CW"
              className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium border border-[hsl(var(--border))] text-[hsl(var(--content-subtle))] hover:bg-[hsl(var(--surface-subtle))] disabled:opacity-50">
              <Download className="h-3 w-3" /> Import
            </button>
          </div>
        )}
        {/* Technician list */}
        <nav className="flex-1 overflow-y-auto p-2 space-y-1">
          {filteredTechs.map((tech) => {
            const isActive = selectedTech?.id === tech.id;
            return (
              <button key={tech.id} onClick={() => setSelectedTechId(tech.id)}
                className={cn('flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-left transition-colors',
                  isActive ? 'bg-brand-50 dark:bg-brand-950/30' : 'hover:bg-[hsl(var(--surface-subtle))]')}>
                <div className="h-9 w-9 rounded-full bg-brand-100 dark:bg-brand-900/30 flex items-center justify-center text-xs font-bold text-brand-700 dark:text-brand-300 shrink-0">
                  {tech.avatarInitials}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={cn('text-sm font-medium truncate', isActive ? 'text-brand-600 dark:text-brand-400' : 'text-[hsl(var(--content))]')}>{tech.name}</p>
                  <p className="text-xs text-[hsl(var(--content-subtle))] truncate">
                    {tech.role ?? '—'}{tech.openTickets != null ? ' · ' + tech.openTickets + ' open' : ''}
                  </p>
                </div>
              </button>
            );
          })}
          {filteredTechs.length === 0 && (
            <p className="text-xs text-[hsl(var(--content-subtle))] text-center py-4">No technicians found</p>
          )}
        </nav>
      </aside>

      {/* Main panel: selected technician details */}
      <div className="flex-1 flex flex-col min-w-0 overflow-y-auto">
        {selectedTech ? (
          <div className="p-6 space-y-5">
            {/* Tech header */}
            <div className="flex items-center gap-4">
              <div className="h-14 w-14 rounded-full bg-brand-100 dark:bg-brand-900/30 flex items-center justify-center text-lg font-bold text-brand-700 dark:text-brand-300 shrink-0">
                {selectedTech.avatarInitials}
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-xl font-bold text-[hsl(var(--content))]">{selectedTech.name}</h2>
                <p className="text-sm text-[hsl(var(--content-subtle))]">
                  {selectedTech.role ?? '—'}{selectedTech.board ? ' · ' + selectedTech.board : ''}{selectedTech.threecxExtension ? ' · 3CX ' + selectedTech.threecxExtension : ''}
                </p>
              </div>
            </div>

            {/* Quick stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-4">
                <p className="text-xs text-[hsl(var(--content-subtle))]">Open Tickets</p>
                <p className="text-2xl font-bold text-[hsl(var(--content))]">{selectedTech.openTickets ?? 0}</p>
              </div>
              <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-4">
                <p className="text-xs text-[hsl(var(--content-subtle))]">Skills Assigned</p>
                <p className="text-2xl font-bold text-[hsl(var(--content))]">{assignedCount}</p>
              </div>
              <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-4">
                <p className="text-xs text-[hsl(var(--content-subtle))]">Expert Level</p>
                <p className="text-2xl font-bold text-emerald-600">{expertCount}</p>
              </div>
              <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-4">
                <p className="text-xs text-[hsl(var(--content-subtle))]">AI-Inferred</p>
                <p className="text-2xl font-bold text-amber-600">{inferredCount}</p>
              </div>
            </div>

            {/* Admin controls */}
            {isAdmin && (
              <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-4 flex items-center gap-4">
                <div>
                  <label className="text-xs text-[hsl(var(--content-subtle))]">Max Open Tickets</label>
                  <input type="number" min="1" defaultValue={selectedTech.maxOpenTickets ?? ''}
                    onBlur={(e) => updateCapacity(selectedTech.id, e.target.value)}
                    className="mt-1 w-32 rounded-lg border border-[hsl(var(--border))] bg-transparent px-3 py-1.5 text-sm text-[hsl(var(--content))]" />
                </div>
                {false && (
                  <div>
                    <label className="text-xs text-[hsl(var(--content-subtle))]">Tier</label>
                    <p className="mt-1 text-sm font-medium text-[hsl(var(--content))]"></p>
                  </div>
                )}
              </div>
            )}

            {/* Skills matrix with expandable groups */}
            <div>
              <h3 className="text-sm font-semibold text-[hsl(var(--content))] mb-3 flex items-center gap-2">
                <Wrench className="h-4 w-4" /> Skills by Board
              </h3>
              <SkillsMatrix
                skills={techSkills}
                canEdit={canEdit}
                onSetLevel={setTechSkill}
              />
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full text-[hsl(var(--content-subtle))]">
            <div className="text-center">
              <TrendingUp className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p className="text-sm">Select a technician from the sidebar</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

